/**
 * <quick-view> — right-side product quick view drawer.
 * Open via data-quick-view-open="{product.handle}" or QuickView.open(handle).
 */
(function () {
  'use strict';

  const isEnabled =
    document.documentElement.getAttribute('data-enable-quick-view') === 'true';

  if (!isEnabled) {
    window.QuickView = {
      open() {},
      close() {},
    };
    return;
  }

  const PANEL_TRANSITION_MS = 300;
  const TAG_NAME = 'quick-view';

  function getQuickView() {
    return document.querySelector(TAG_NAME);
  }

  class QuickView extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-quick-view-overlay]');
      this.panel = this.querySelector('[data-quick-view-panel]');
      this.root = this.querySelector('[data-quick-view-root]');
      this.loading = this.querySelector('[data-quick-view-loading]');
      this.sectionId = this.dataset.sectionId || 'quick-view-product';
      this.errorMessage =
        this.dataset.labelError || 'Unable to load product. Please try again.';

      this.querySelectorAll('[data-quick-view-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      this.bindCartWatcher();
    }

    bindCartWatcher() {
      if (this._cartWatcherBound) return;

      const attach = () => {
        if (!window.Alpine?.store || !window.Alpine.effect) return false;
        if (this._cartWatcherBound) return true;
        this._cartWatcherBound = true;
        window.Alpine.effect(() => {
          const cart = window.Alpine.store('cart');
          if (cart?.isOpen && this.isOpen) {
            this.close();
          }
        });
        return true;
      };

      if (!attach()) {
        document.addEventListener('alpine:initialized', () => attach(), { once: true });
      }
    }

    get isOpen() {
      return !this.hasAttribute('hidden');
    }

    open(handle) {
      if (!handle) return;
      this.removeAttribute('hidden');
      document.body.classList.add('quick-view-open');
      requestAnimationFrame(() => {
        this.panel?.classList.remove('translate-x-full');
      });
      this.loadProduct(handle);
    }

    close() {
      if (!this.isOpen) return;
      this.panel?.classList.add('translate-x-full');
      document.body.classList.remove('quick-view-open');
      window.setTimeout(() => {
        this.setAttribute('hidden', '');
        if (this.root) this.root.innerHTML = '';
        if (this.loading) this.loading.hidden = true;
      }, PANEL_TRANSITION_MS);
    }

    async loadProduct(handle) {
      if (!this.root) return;
      if (this.loading) this.loading.hidden = false;
      this.root.innerHTML = '';

      try {
        const url = `/products/${encodeURIComponent(handle)}?sections=${this.sectionId}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Quick view fetch failed: ${response.status}`);

        const data = await response.json();
        const html = data[this.sectionId];
        if (!html) throw new Error(`Section "${this.sectionId}" not found in response`);

        this.root.innerHTML = html;

        if (window.Alpine?.initTree) {
          window.Alpine.initTree(this.root);
        }
      } catch (error) {
        console.error('[quick-view] load failed', error);
        this.root.innerHTML = `<p class="p-8 text-center text-small">${this.errorMessage}</p>`;
      } finally {
        if (this.loading) this.loading.hidden = true;
      }
    }
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, QuickView);
  }

  function openFromOpener(opener) {
    if (!isEnabled) return;

    const handle = opener?.getAttribute('data-quick-view-open');
    if (!handle) return;

    const el = getQuickView();
    if (!el) {
      console.warn('[quick-view] <quick-view> element not found in DOM');
      return;
    }

    el.open(handle);
  }

  document.addEventListener(
    'click',
    (event) => {
      const opener = event.target.closest('[data-quick-view-open]');
      if (!opener) return;
      event.preventDefault();
      event.stopPropagation();
      openFromOpener(opener);
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    getQuickView()?.close();
  });

  window.QuickView = {
    open(handle) {
      if (!isEnabled) return;
      getQuickView()?.open(handle);
    },
    close() {
      if (!isEnabled) return;
      getQuickView()?.close();
    },
  };
})();